import React, { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { observabilityApi } from '../../../services/api';
import { RefreshCw } from 'lucide-react';
import ReactFlow, {
  Background, Controls, MiniMap,
  MarkerType, Node, Edge, Position,
} from 'reactflow';
import 'reactflow/dist/style.css';

/* Compute horizontal layered layout — roots at left, leaves at right.
   Uses Kahn's topo-sort to assign a column (depth) to every node,
   then stacks them vertically within each column. */
function layoutHorizontal(nodes: any[], edges: any[]) {
  const incoming: Record<string, number> = {};
  const outgoing: Record<string, string[]> = {};
  nodes.forEach((n) => { incoming[n.id] = 0; outgoing[n.id] = []; });
  edges.forEach((e) => {
    if (incoming[e.target] == null) incoming[e.target] = 0;
    if (outgoing[e.source] == null) outgoing[e.source] = [];
    incoming[e.target]++;
    outgoing[e.source].push(e.target);
  });
  const level: Record<string, number> = {};
  const queue: string[] = [];
  nodes.forEach((n) => {
    if (incoming[n.id] === 0) { level[n.id] = 0; queue.push(n.id); }
  });
  while (queue.length) {
    const id = queue.shift()!;
    for (const child of outgoing[id] || []) {
      level[child] = Math.max(level[child] ?? 0, (level[id] ?? 0) + 1);
      incoming[child]--;
      if (incoming[child] === 0) queue.push(child);
    }
  }
  // Any orphan cycles — pin them at level 0
  nodes.forEach((n) => { if (level[n.id] == null) level[n.id] = 0; });
  // Group by level for vertical stacking
  const byLevel: Record<number, string[]> = {};
  Object.entries(level).forEach(([id, lv]) => {
    byLevel[lv] = byLevel[lv] || [];
    byLevel[lv].push(id);
  });
  const pos: Record<string, { x: number; y: number }> = {};
  const COL_W = 320;
  const ROW_H = 140;
  // Find the max stack height across all columns — use for centering
  const maxStack = Math.max(...Object.values(byLevel).map((ids) => ids.length));
  const centerOffset = (maxStack - 1) * ROW_H / 2;
  Object.entries(byLevel).forEach(([lv, ids]) => {
    const x = parseInt(lv) * COL_W;
    // Vertically center each column: if this column has fewer nodes than maxStack,
    // shift them down so they centre-align with the tallest column.
    const colCount = ids.length;
    const colOffset = (maxStack - colCount) * ROW_H / 2;
    ids.forEach((id, i) => {
      pos[id] = { x, y: colOffset + i * ROW_H };
    });
  });
  return pos;
}

const statusColors: Record<string, { bg: string; border: string; text: string }> = {
  healthy: { bg: '#ecfdf5', border: '#10b981', text: '#065f46' },
  warning: { bg: '#fffbeb', border: '#f59e0b', text: '#78350f' },
  critical: { bg: '#fef2f2', border: '#ef4444', text: '#7f1d1d' },
};

const LineageTab: React.FC<{ projectId?: number }> = ({ projectId }) => {
  const qc = useQueryClient();
  const [autoDiscovered, setAutoDiscovered] = React.useState(false);

  const q = useQuery({
    queryKey: ['obs-lineage', projectId ?? 'all'],
    queryFn: async () => (await observabilityApi.lineage(projectId)).data.data,
    refetchInterval: 60000,
  });

  // Auto-trigger discovery on first visit if lineage is empty
  React.useEffect(() => {
    if (!q.isLoading && q.data && !autoDiscovered) {
      const hasEdges = (q.data.edges || []).length > 0;
      if (!hasEdges) {
        setAutoDiscovered(true);
        observabilityApi.discoverLineage().then(() => {
          qc.invalidateQueries({ queryKey: ['obs-lineage'] });
        });
      } else {
        setAutoDiscovered(true);
      }
    }
  }, [q.isLoading, q.data, autoDiscovered, qc]);

  const discover = async () => {
    await observabilityApi.discoverLineage();
    qc.invalidateQueries({ queryKey: ['obs-lineage'] });
  };

  const { nodes, edges, blast } = useMemo(() => {
    if (!q.data) return { nodes: [], edges: [], blast: {} };
    const nodes = q.data.nodes || [];
    const edges = q.data.edges || [];
    const blast = q.data.blast_radius || {};
    return { nodes, edges, blast };
  }, [q.data]);

  // Set of node ids that are "along a cascade path" — either directly
  // unhealthy (critical/warning) OR appear in some blast radius (AT RISK).
  // Edges originating from any of these go red so the full cascade trail
  // is visible end-to-end, not just the first hop.
  const cascadeSources = useMemo(() => {
    const s = new Set<string>();
    for (const n of nodes) {
      if (n.status === 'critical' || n.status === 'warning') s.add(n.id);
    }
    for (const arr of Object.values(blast) as string[][]) {
      for (const id of arr || []) s.add(id);
    }
    return s;
  }, [nodes, blast]);

  // layout: grid-based — group tables per source, columns
  const rfNodes: Node[] = useMemo(() => {
    const pos = layoutHorizontal(nodes, edges);
    // Amber palette used when a healthy node is "AT RISK" via blast radius.
    const atRiskColor = { bg: '#fffbeb', border: '#f59e0b', text: '#b45309' };
    return nodes.map((n: any) => {
      const isGhost = n.monitored === false;
      const inBlast = Object.values(blast).some((arr: any) => arr?.includes(n.id));
      // "At risk" = node itself is healthy, but it's downstream of something
      // unhealthy. Distinguishes "potentially impacted" from "actually impacted".
      const isAtRisk = !isGhost && inBlast && n.status === 'healthy';
      const color = isGhost
        ? { bg: '#f9fafb', border: '#9ca3af', text: '#6b7280' }
        : isAtRisk
        ? atRiskColor
        : (statusColors[n.status] || statusColors.healthy);
      const displayStatus = isGhost ? 'DISCOVERED' : isAtRisk ? 'AT RISK' : n.status;
      return {
        id: n.id,
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        data: { label: (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 600, fontSize: 13,
                          fontStyle: isGhost ? 'italic' : 'normal',
                          opacity: isGhost ? 0.7 : 1 }}>
              {n.label}
            </div>
            <div style={{ fontSize: 10, opacity: 0.7 }}>
              {n.schema || n.source_id}
            </div>
            <div style={{
              fontSize: 9, fontWeight: 700, marginTop: 4,
              color: color.text, textTransform: 'uppercase',
            }}>
              {displayStatus}
            </div>
          </div>
        )},
        position: pos[n.id] || { x: 0, y: 0 },
        style: {
          background: color.bg,
          border: `${isGhost ? '2px dashed' : '2px solid'} ${color.border}`,
          borderRadius: 10,
          padding: '10px 16px',
          minWidth: 200,
          maxWidth: 260,
          width: 'auto',
          wordBreak: 'break-word',
          whiteSpace: 'normal',
          boxShadow: inBlast ? '0 0 0 3px rgba(239,68,68,.3)' : '0 1px 3px rgba(0,0,0,0.1)',
        },
      };
    });
  }, [nodes, edges, blast]);

  const rfEdges: Edge[] = useMemo(() =>
    edges.map((e: any, i: number) => {
      // Cascade edges: any edge whose SOURCE is either directly unhealthy
      // or in a blast radius gets red-styled. This lights up the full
      // cascade path end-to-end — without this rule the red trail stops
      // at the first "AT RISK" node even though its children are also
      // flagged, breaking the visual story.
      const isCascade = cascadeSources.has(e.source);
      const color = isCascade ? '#ef4444' : '#6366f1';
      return {
        id: `e${i}`,
        source: e.source,
        target: e.target,
        label: e.label,
        animated: true,
        style: { stroke: color, strokeWidth: isCascade ? 2 : 1 },
        markerEnd: { type: MarkerType.ArrowClosed, color },
      };
    }), [edges, cascadeSources]);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-cargo-border p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-semibold">🗺️ Lineage Graph</h3>
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"></span>Healthy</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#f59e0b' }}></span>At risk</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"></span>Warning</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500"></span>Critical</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded border-2 border-dashed border-gray-400"></span>Discovered</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ boxShadow: '0 0 0 2px rgba(239,68,68,.4)' }}></span>Blast radius</span>
            <button onClick={discover}
              title="Re-scan query logs and refresh lineage"
              className="ml-3 flex items-center gap-1 px-2.5 py-1 rounded border border-cargo-border hover:bg-gray-50">
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>
        </div>
        {q.isLoading ? (
          <div className="text-sm text-cargo-muted py-8 text-center">Loading...</div>
        ) : rfNodes.length === 0 ? (
          <div className="text-sm text-cargo-muted py-8 text-center">
            No monitored tables yet. Register tables to see lineage.
          </div>
        ) : (
          <div style={{ width: '100%', height: 560 }}>
            <ReactFlow
              nodes={rfNodes}
              edges={rfEdges}
              fitView
              attributionPosition="bottom-left"
            >
              <Background gap={16} color="#e5e7eb" />
              <Controls />
              <MiniMap nodeColor={(n) => {
                const node = nodes.find((x: any) => x.id === n.id);
                return statusColors[node?.status]?.border || '#9ca3af';
              }} />
            </ReactFlow>
          </div>
        )}
        <div className="text-xs text-cargo-muted mt-3">
          Edges are derived from foreign-key relationships in your source schema.
          Blast radius highlights downstream tables of any unhealthy node.
        </div>
      </div>

      {Object.keys(blast).length > 0 && (
        <div className="bg-white rounded-xl border border-cargo-border p-5">
          <h3 className="font-semibold mb-3">💥 Impact Summary (Blast Radius)</h3>
          <ul className="space-y-2 text-sm">
            {Object.entries(blast).map(([node, downstream]: any) => (
              <li key={node}>
                <span className="font-semibold">{node.split(':').pop()}</span>
                {' → affects '}
                <span className="text-red-600 font-semibold">{downstream.length}</span>
                {' downstream table(s): '}
                <span className="text-cargo-muted">{downstream.map((d: string) => d.split(':').pop()).join(', ')}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default LineageTab;
